import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_V1_PREFIX, isProd } from '../../utils/constants';
import Footer from '../../components/common/Footer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getProductType, getPageType, getUTMs } from '../../utils/dataLayer';
import AccordArrow from '../../assets/icon-components/AccordArrow';
import checkIcon from '../../assets/icons/CheckTick.svg';
import arrowRightIcon from '../../assets/icons/whiteArrow.png';
import bannerImg1 from '../../assets/images/BannerCoreVitalsMembership.png';
import bannerImg2 from '../../assets/images/BannerCoreVitalsPlusMembership.png';
import bannerImg3 from '../../assets/images/BannerFullBodyMRI.png';
import './CompleteOnboarding.scss';

const plans = [
  {
    id: 'coreVitals',
    title: 'CoreVitals Membership',
    description: 'Ongoing health tracking without imaging - up to 4x blood tests/year',
    bannerImg: bannerImg1,
    welcomeMessage: 'Welcome to CoreVitals',
    headingMessage: 'Thanks for joining us,',
    list: [
      '1 blood biomarker test per year',
      'Includes free nurse blood draw at home',
      'Full biomarker panel',
      'Doctor review + consult',
      'Nutritionist guidance',
      'App access to track progress',
    ],
    nextSteps: [
      "Complete the onboarding form",
      "Book a home blood draw with our nurses, or visit one of our partner clinics across the UK",
      "Review your detailed results in the CoreVitals app",
      "Have a 1:1 call with our GP and nutritionist for personalised insights, and recommendations."
    ],
  },
  {
    id: 'coreVitalsPlus',
    title: 'CoreVitals+ Membership',
    description: 'CoreVitals+ Membership – Deeper insight across bloods combined with MRI imaging.',
    bannerImg: bannerImg2,
    welcomeMessage: 'Welcome to CoreVitals',
    headingMessage: 'Thanks for joining us,',
    list: [
      '1 blood biomarker test per year',
      'Includes free nurse blood draw at home',
      'Full biomarker panel',
      'Doctor review + consult',
      'Nutritionist guidance',
      'App access to track progress',
      'Annual full-body MRI',
      'Pre- and post-scan clinician consults',
      'Combined blood and MRI insights in-app',
      'Ongoing tracking and progress monitoring'
    ],
    nextSteps: [
      "Complete the onboarding form",
      "Book a home blood draw with our nurses, or visit one of our partner clinics across the UK",
      "Book a full body scan at one of our numerous partner scan centres across the UK",
      "Review your detailed results in the CoreVitals app",
      "Have a 1:1 call with our GP and nutritionist to review results, personalised insights, and recommendations."
    ],
  },
  {
    id: 'fullBody',
    title: 'Full-body MRI',
    description: 'A one-time internal snapshot',
    bannerImg: bannerImg3,
    welcomeMessage: 'Thank you for using CoreVitals.',
    headingMessage: "Let's get you scanned,",
    list: [
      'In-app full-body scan report',
      'Doctor review + consult',
      'Pre and post scan clinician consults',
      'Report organised by body part',
      '60-90 minutes in the scanner',
    ],
    nextSteps: [
      "Complete the onboarding form",
      "Our team calls you.<br />We complete GP safety screening.<br />We confirm your scan date and time.",
      "Your radiology results upload to the CoreVitals app.",
      "You have a 1:1 GP call.<br />You review results, insights, and next steps."
    ],
  },
];

const ConfirmationOrderSummary = ({ 
  selectedPlanId, 
  productName,
  numberOfTests
}: { 
  selectedPlanId: string;
  productName?: string | null;
  numberOfTests?: number | null;
}) => {
  const selectedPlan = plans.find(plan => plan.id === selectedPlanId);
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (!selectedPlan) {
    return null;
  }
  
  // Build display title with number of tests for core plans
  let displayTitle = productName || selectedPlan.title;
  if ((selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus') && numberOfTests) {
    displayTitle = `${selectedPlan.title} ${numberOfTests} test${numberOfTests > 1 ? 's' : ''}`;
  }
  
  let itemsToShow = selectedPlan.list;
  
  // Dynamically update the number of tests in the list
  if (numberOfTests && (selectedPlan.id === 'coreVitals' || selectedPlan.id === 'coreVitalsPlus')) {
    itemsToShow = itemsToShow.map(item => {
      // Replace "1 blood biomarker test per year" with the dynamic number
      if (item.includes('blood biomarker test per year')) {
        return `${numberOfTests} blood biomarker test${numberOfTests > 1 ? 's' : ''} per year`;
      }
      return item;
    });
  }
  
  return (
    <div className='plan-info'>
      <div className='order-summary-banner'>
        <img src={selectedPlan.bannerImg} alt={`${displayTitle} banner`} />
      </div>
      {!isMobile && <div className='order-summary-title'>{displayTitle}</div>}
      {!isMobile && <p className='order-summary-description'>{selectedPlan.description}</p>}
      <ul className='order-summary-list'>
        {itemsToShow.map(item => (
          <li key={item}>
            <img src={checkIcon} alt='check' />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

const CompleteOnboarding: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [memberDetails, setMemberDetails] = useState<any>({});
  const [selectedPlanType, setSelectedPlanType] = useState<string>('');
  const [productName, setProductName] = useState<string | null>(null);
  const [numberOfTests, setNumberOfTests] = useState<number | null>(null);
  const [showOrderSummary, setShowOrderSummary] = useState(true);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');
  
  const urlParams = new URLSearchParams(window.location.search);
  const tokenParam = urlParams.get('token');

  // Map membership_type from API to plan ID
  const mapMembershipTypeToPlanId = (membershipType: string | null | undefined): string => {
    if (!membershipType) return '';
    
    const type = membershipType.toLowerCase();
    if (type === 'scan') return 'fullBody';
    if (type === 'core') return 'coreVitals';
    if (type === 'plus') return 'coreVitalsPlus';
    
    return '';
  };

  useEffect(() => {
    if (tokenParam) {
      setToken(tokenParam);
      fetchMemberData(tokenParam);
    } else {
      setIsLoading(false);
    }
  }, [tokenParam]);

  useEffect(() => {
    // Fallback: push purchase event after Stripe redirect.
    // This page is the return_url for `/full-body-scan` payments, and in redirect flows
    // `onComplete()` on the payment page may never run.
    if (!memberDetails || Object.keys(memberDetails).length === 0) return;

    const dedupeKey = `purchase_fired_${tokenParam || 'no_token'}`;
    if (sessionStorage.getItem(dedupeKey)) return;

    // Only run this fallback when coming back from Stripe redirect,
    // otherwise we might fire on normal navigations.
    const redirectStatus = urlParams.get('redirect_status');
    const hasStripeIntent =
      !!urlParams.get('payment_intent') ||
      !!urlParams.get('setup_intent') ||
      !!urlParams.get('payment_intent_client_secret') ||
      !!urlParams.get('setup_intent_client_secret');
    if (!(redirectStatus === 'succeeded' && hasStripeIntent)) return;

    const signupResponse = localStorage.getItem('signupResponse');
    const subscriptionAmountStr = localStorage.getItem('subscriptionAmount');
    const pendingPurchaseStr = tokenParam ? sessionStorage.getItem(`pending_purchase_${tokenParam}`) : null;

    // Prefer amount from signupResponse; fallback to subscriptionAmount.
    let amount: number | null = null;
    let parsedSignupData: any | null = null;

    if (signupResponse) {
      try {
        parsedSignupData = JSON.parse(signupResponse);
        if (parsedSignupData?.amount !== undefined && parsedSignupData?.amount !== null) {
          amount = Number(parsedSignupData.amount);
        }
      } catch (error) {
        console.error('Error parsing signup response:', error);
        localStorage.removeItem('signupResponse');
      }
    }

    if ((amount == null || Number.isNaN(amount)) && subscriptionAmountStr) {
      const parsed = Number(subscriptionAmountStr);
      if (!Number.isNaN(parsed)) amount = parsed;
    }

    if ((amount == null || Number.isNaN(amount)) && pendingPurchaseStr) {
      try {
        const pending = JSON.parse(pendingPurchaseStr);
        const pendingAmount = Number(pending?.amount);
        if (!Number.isNaN(pendingAmount)) amount = pendingAmount;
      } catch {
        // ignore
      }
    }

    if (amount == null || Number.isNaN(amount)) return;

    const path = window.location.pathname;
    window.dataLayer.push({
      event: 'purchase',
      url: window.location.href,
      host: window.location.host,
      path,
      product_type: getProductType(path, memberDetails?.membership_type),
      page_type: getPageType(path),
      checkout_step: 'none',
      ...getUTMs(),
      referrer: document.referrer || null,
      member_details: memberDetails,
      membership_type: memberDetails?.membership_type,
      amount,
    });

    sessionStorage.setItem(dedupeKey, '1');
    if (tokenParam) {
      sessionStorage.removeItem(`pending_purchase_${tokenParam}`);
    }

    // Keep existing behavior: remove signupResponse after firing, so we don't accidentally re-fire elsewhere.
    if (signupResponse) {
      localStorage.removeItem('signupResponse');
    }
  }, [memberDetails, tokenParam]);

  const fetchMemberData = async (token: string) => {
    try {
      setIsLoading(true);
      const baseURL = isProd
        ? process.env.REACT_APP_API_BASE_URL_PROD
        : process.env.REACT_APP_API_BASE_URL_DEV;

      const response = await axios.get(
        `${baseURL}/${API_V1_PREFIX}members/?load_strategy=with_subscription`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const memberData = response.data;
      setMemberDetails(memberData);

      let storedPlan: {
        type?: string;
        testCount?: number | null;
        product?: { name?: string | null } | null;
      } | null = null;
      try {
        const rawPlan = localStorage.getItem('selectedPlan');
        if (rawPlan) {
          storedPlan = JSON.parse(rawPlan);
        }
      } catch {
        storedPlan = null;
      }

      let planId = mapMembershipTypeToPlanId(memberData.membership_type);
      if (!planId) {
        const st = storedPlan?.type;
        if (st === 'coreVitals' || st === 'coreVitalsPlus' || st === 'fullBody') {
          planId = st;
        }
      }
      setSelectedPlanType(planId);

      if (memberData.number_of_blood_test_plan) {
        setNumberOfTests(memberData.number_of_blood_test_plan);
      } else if (storedPlan?.testCount && storedPlan.testCount > 0) {
        setNumberOfTests(storedPlan.testCount);
      } else {
        setNumberOfTests(null);
      }

      if (memberData.product_name) {
        setProductName(memberData.product_name);
      } else if (storedPlan?.product?.name) {
        setProductName(storedPlan.product.name ?? null);
      }
    } catch (error) {
      console.error('Error fetching member data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className='main-wrapper'>
        <div className='loading-message'>Loading...</div>
      </div>
    );
  }

  const selectedPlanData = plans.find(plan => plan.id === selectedPlanType);

  return (
    <>
      <div className='c_container subscription-page-wrapper'>
        <div className='two-colum-grid'>
          <div className='left-column'>
            <div className='journey-starts-here'>
              {selectedPlanData?.welcomeMessage || 'Welcome to CoreVitals'}
            </div>
            <h2 className='select-plan-title'>
              {selectedPlanData?.headingMessage || 'Thanks for joining us,'} {memberDetails.first_name || 'there'}!
            </h2>
            {isMobile && (
              <div className={`inner-card order-summary-card ${showOrderSummary ? 'active' : ''}`}>
                <h4 
                  className='inner-card-title' 
                  onClick={() => setShowOrderSummary(!showOrderSummary)}
                >
                  Order summary <AccordArrow />
                </h4>
                {!showOrderSummary && (
                  <div className='order-summary-description'>
                    {(() => {
                      if (!selectedPlanData) return 'Order summary';
                      let title = selectedPlanData.title;
                      if ((selectedPlanData.id === 'coreVitals' || selectedPlanData.id === 'coreVitalsPlus') && numberOfTests) {
                        title = `${selectedPlanData.title} ${numberOfTests} test${numberOfTests > 1 ? 's' : ''}`;
                      }
                      return title;
                    })()}
                  </div>
                )}
                {showOrderSummary && selectedPlanType && (
                  <ConfirmationOrderSummary 
                    selectedPlanId={selectedPlanType}
                    productName={productName}
                    numberOfTests={numberOfTests}
                  />
                )}
              </div>
            )}
            {selectedPlanData && (
              <div className={`confirmation-steps ${selectedPlanData.id}`}>
                <h4 className='confirmation-steps-title'>Next steps:</h4>
                {selectedPlanData.nextSteps?.map((stepText, index) => (
                  <div key={`${stepText}-${index}`}>
                    <h5>Step {index + 1}</h5>
                    <p dangerouslySetInnerHTML={{ __html: stepText }} />
                  </div>
                ))}
              </div>
            )}
            <div className='help-section-pricing'>
              <h3>Need help?</h3>
              <p>
                If you have any questions, just reply to your confirmation email
                or reach out at&nbsp;
                <a href='mailto:members@corevitals.me'>members@corevitals.me</a>
              </p>
            </div>
            <div>
              <button
                onClick={() => navigate(`/complete-profile?token=${token}`)}
                className='plans-step-btn top40'
              >
                Continue to onboarding form <img src={arrowRightIcon} alt='arrow right' />
              </button>
            </div>
          </div>
          {!isMobile && (
            <div className='right-column'>
              <div className='inner-card'>
                {selectedPlanType && (
                  <ConfirmationOrderSummary 
                    selectedPlanId={selectedPlanType}
                    productName={productName}
                    numberOfTests={numberOfTests}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CompleteOnboarding;

